import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * InstallPrompt — listens for the `beforeinstallprompt` event (Chromium +
 * Edge + Samsung Internet), stashes it, and surfaces an "Install RoadBridge"
 * button when the PWA install criteria are met.
 *
 * Safari/iOS doesn't fire `beforeinstallprompt` — the user installs via the
 * share sheet ("Add to Home Screen"). A follow-up ticket (ROADS-151) adds an
 * iOS-specific hint.
 */

// Minimal shape of the Chromium-specific BeforeInstallPromptEvent.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !deferred) return null;

  const onClick = async () => {
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  return (
    <div className="fixed bottom-md end-md">
      <button
        type="button"
        onClick={onClick}
        className="rounded-md bg-brand-primary text-white px-md py-sm shadow-lg hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("pwa.install")}
      </button>
    </div>
  );
}
